import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FiMaximize2, FiPlus, FiSearch, FiX } from 'react-icons/fi';
import { Sparkles, ListChecks } from 'lucide-react';
import { getApiError } from '@/utils/errorUtils';
import {
  useCreateFollowUpMutation,
  useGetFollowUpsQuery,
  useParseFollowUpMutation,
  useCompleteFollowUpMutation,
  usePostponeFollowUpMutation,
  useDeleteFollowUpMutation,
} from '@/store/api/apiSlice';
import FollowUpRow from './FollowUpRow';
import {
  countTodayScope,
  countUpcomingScope,
  filterFollowUpsByTab,
  parsePreviewToDisplay,
  tabForDueDate,
  toFollowUpDisplay,
} from './followUpUtils';
import type { FollowUpParsePreview, FollowUpTab, PostponePreset } from './types';
import './followups-panel.css';

type TodayPanelLayout = 'popover' | 'fullscreen';

type TodayPanelProps = {
  className?: string;
  layout?: TodayPanelLayout;
  onExpand?: () => void;
  onClose?: () => void;
};

type PanelTab = FollowUpTab | 'all';

function matchesSearch(
  item: { title: string; note?: string | null; waitingOnName?: string | null },
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    item.title.toLowerCase().includes(q) ||
    (item.note?.toLowerCase().includes(q) ?? false) ||
    (item.waitingOnName?.toLowerCase().includes(q) ?? false)
  );
}

export default function TodayPanel({
  className = '',
  layout = 'popover',
  onExpand,
  onClose,
}: TodayPanelProps) {
  const { t } = useTranslation();
  const isFullscreen = layout === 'fullscreen';
  const { data: items = [], isLoading } = useGetFollowUpsQuery();
  const [parseFollowUp, { isLoading: parsing }] = useParseFollowUpMutation();
  const [createFollowUp, { isLoading: creating }] = useCreateFollowUpMutation();
  const [completeFollowUp] = useCompleteFollowUpMutation();
  const [postponeFollowUp] = usePostponeFollowUpMutation();
  const [deleteFollowUp] = useDeleteFollowUpMutation();

  const [tab, setTab] = useState<PanelTab>('today');
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState('');
  const [parsePreview, setParsePreview] = useState<FollowUpParsePreview | null>(null);
  const [parseUsedAi, setParseUsedAi] = useState(true);
  const [showManual, setShowManual] = useState(false);
  const [manualTitle, setManualTitle] = useState('');

  const todayCount = useMemo(() => countTodayScope(items), [items]);
  const upcomingCount = useMemo(() => countUpcomingScope(items), [items]);
  const doneCount = useMemo(
    () => items.filter((i) => i.status === 'DONE').length,
    [items],
  );

  const filtered = useMemo(() => {
    if (tab === 'all') {
      return items
        .map((item) => toFollowUpDisplay(item))
        .filter((item) => matchesSearch(item, search))
        .sort((a, b) => {
          if (a.status === 'DONE' && b.status !== 'DONE') return 1;
          if (b.status === 'DONE' && a.status !== 'DONE') return -1;
          const ad = a.dueDate ? new Date(a.dueDate).getTime() : Number.MAX_SAFE_INTEGER;
          const bd = b.dueDate ? new Date(b.dueDate).getTime() : Number.MAX_SAFE_INTEGER;
          return ad - bd;
        });
    }
    const tabItems = filterFollowUpsByTab(items, tab as FollowUpTab);
    return tabItems.filter((item) => matchesSearch(item, search));
  }, [items, tab, search]);

  const previewDisplay = parsePreview ? parsePreviewToDisplay(parsePreview) : null;

  const runParse = async () => {
    const text = draft.trim();
    if (!text) return;
    try {
      const result = await parseFollowUp({ text }).unwrap();
      setParsePreview(result);
      setParseUsedAi(result.aiUsed !== false);
    } catch {
      setParsePreview(null);
      toast.error(t('todayPanel.parseFailed'));
    }
  };

  const confirmParsed = async () => {
    if (!parsePreview) return;
    try {
      await createFollowUp({
        title: parsePreview.title,
        note: parsePreview.note || draft.trim(),
        dueDate: parsePreview.dueDate || undefined,
        ...(parsePreview.waitingOnUserId ? { waitingOnUserId: parsePreview.waitingOnUserId } : {}),
        ...(parsePreview.waitingOnName ? { waitingOnName: parsePreview.waitingOnName } : {}),
      }).unwrap();
      setDraft('');
      setParsePreview(null);
      setTab(tabForDueDate(parsePreview.dueDate));
      toast.success(t('todayPanel.created'));
    } catch (error) {
      toast.error(getApiError(error, t('todayPanel.createFailed')));
    }
  };

  const addManual = async () => {
    const title = manualTitle.trim();
    if (!title) return;
    try {
      await createFollowUp({ title }).unwrap();
      setManualTitle('');
      setShowManual(false);
      setTab('today');
      toast.success(t('todayPanel.created'));
    } catch (error) {
      toast.error(getApiError(error, t('todayPanel.createFailed')));
    }
  };

  const markDone = async (id: string) => {
    await completeFollowUp(id).unwrap().catch(() => null);
  };

  const postpone = async (id: string, preset: PostponePreset) => {
    await postponeFollowUp({ id, preset }).unwrap().catch(() => null);
    setTab('upcoming');
  };

  const remove = async (id: string) => {
    await deleteFollowUp(id).unwrap().catch(() => null);
  };

  const tabs: { id: PanelTab; label: string; count: number }[] = [
    { id: 'today', label: t('todayPanel.tabToday'), count: todayCount },
    { id: 'upcoming', label: t('todayPanel.tabUpcoming'), count: upcomingCount },
    { id: 'done', label: t('todayPanel.tabDone'), count: doneCount },
    ...(isFullscreen ? [{ id: 'all' as const, label: t('todayPanel.tabAll'), count: items.length }] : []),
  ];

  const busy = parsing || creating;

  return (
    <div className={`fu-panel${isFullscreen ? ' fu-fullscreen' : ''} ${className}`.trim()}>
      <div className="fu-head">
        <h2 className="fu-title">{t('todayPanel.fullscreenTitle')}</h2>
        <div className="fu-acts">
          {!isFullscreen && onExpand && (
            <button type="button" onClick={onExpand} title={t('todayPanel.expand')} aria-label={t('todayPanel.expand')}>
              <FiMaximize2 />
            </button>
          )}
          {onClose && (
            <button type="button" onClick={onClose} aria-label={t('common.close')}>
              <FiX />
            </button>
          )}
        </div>
      </div>

      <div className="fu-tabs">
        {tabs.map((tItem) => (
          <button
            key={tItem.id}
            type="button"
            className={`fu-tab${tab === tItem.id ? ' on' : ''}`}
            onClick={() => setTab(tItem.id)}
          >
            {tItem.label} <span className="fu-n">{tItem.count}</span>
          </button>
        ))}
      </div>

      {isFullscreen && (
        <div className="fu-search">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('todayPanel.searchPlaceholder')}
          />
        </div>
      )}

      <div className="fu-addbar">
        <div className="fu-field">
          <Sparkles strokeWidth={2} />
          <input
            type="text"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setParsePreview(null);
              setParseUsedAi(true);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !busy) void runParse();
            }}
            placeholder={t('todayPanel.inputPlaceholder')}
          />
        </div>
        <button type="button" className="fu-addbtn" onClick={() => void runParse()} disabled={busy || !draft.trim()}>
          <FiPlus />
          {t('todayPanel.add')}
        </button>
      </div>

      {parsePreview && previewDisplay && (
        <div className="fu-preview">
          <div className="fu-preview-label">
            {parseUsedAi ? t('todayPanel.aiPreview') : t('todayPanel.aiFallbackHint')}
          </div>
          <div className="fu-preview-title">{previewDisplay.title}</div>
          <div className="fu-preview-meta">
            {previewDisplay.dueLabel}
            {previewDisplay.waitingOn ? ` · ${previewDisplay.waitingOn}` : ''}
          </div>
          <div className="fu-preview-actions">
            <button type="button" className="fu-confirm" onClick={() => void confirmParsed()} disabled={creating}>
              {t('todayPanel.confirm')}
            </button>
            <button type="button" className="fu-cancel" onClick={() => setParsePreview(null)}>
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      <div className="fu-list">
        {isLoading ? (
          <div className="fu-empty">{t('common.loading')}</div>
        ) : filtered.length === 0 ? (
          <div className="fu-empty">
            <ListChecks strokeWidth={1.5} />
            {search.trim()
              ? t('todayPanel.emptySearch')
              : tab === 'today'
                ? t('todayPanel.emptyToday')
                : tab === 'upcoming'
                  ? t('todayPanel.emptyUpcoming')
                  : tab === 'all'
                    ? t('todayPanel.emptyAll')
                    : t('todayPanel.emptyDone')}
          </div>
        ) : (
          filtered.map((item) => (
            <FollowUpRow
              key={item.id}
              item={item}
              onComplete={markDone}
              onPostpone={postpone}
              onDelete={remove}
            />
          ))
        )}
      </div>

      {showManual ? (
        <div className="fu-manual-row">
          <input
            type="text"
            value={manualTitle}
            onChange={(e) => setManualTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !creating && void addManual()}
            placeholder={t('todayPanel.manualPlaceholder')}
            autoFocus
          />
          <button type="button" className="fu-addbtn" onClick={() => void addManual()} disabled={creating || !manualTitle.trim()}>
            {t('common.save')}
          </button>
        </div>
      ) : (
        <button type="button" className="fu-footer" onClick={() => setShowManual(true)}>
          <FiPlus />
          {t('todayPanel.addManually').replace(/^\+ /, '')}
        </button>
      )}
    </div>
  );
}
